import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useLoading } from '../providers/loading'
import diagramApi from '../api/diagram'
import Select from '../components/select'

const DiagramsPage = () => {
  const [searchParams] = useSearchParams()
  const { setIsLoading } = useLoading()
  const navigate = useNavigate()

  const [categories, setCategories] = useState([])
  const [title, setTitle] = useState('')

  const [selectedCategory, setSelectedCategory] = useState(null)
  const [searchText, setSearchText] = useState('')

  const engineType = searchParams.get('engine_type')
  const enginePower = searchParams.get('engine_power')
  const model = searchParams.get('model')
  const year = searchParams.get('year')
  const category = searchParams.get('category')

  const isFilterActive = selectedCategory || searchText.trim()

  const fetchDiagrams = async () => {
    setIsLoading(true)

    try {
      const response = await diagramApi.search({
        engine_type: engineType,
        engine_power: enginePower,
        model,
        year,
        category
      })

      if (response?.success && response.data) {
        setCategories(response.data.categories)
        setTitle(response.data.title)
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleBack = () => {
    window.parent.postMessage({
      'action': 'redirect',
      'data': {
        'url': '/'
      }
    }, '*')

    // navigate('/')
  }

  const getFilteredCategories = () => {
    if (!categories.length) {
      return []
    }

    if (!isFilterActive) {
      return categories
    }

    return categories
      .map(category => {
        const filteredProducts = searchText 
          ? category.products.filter(product => 
              product.product_name?.toLowerCase().includes(searchText.toLowerCase()) ||
              product.sku?.toLowerCase().includes(searchText.toLowerCase())
            )
          : category.products

        if ((selectedCategory && selectedCategory !== category._id) || 
            (searchText && !filteredProducts.length)) {
          return null
        }

        return {
          ...category,
          products: filteredProducts
        }
      })
      .filter(Boolean)
  }

  const handleOpenDiagramCategory = (diagramCategory, selectedProductId = null) => {
    const searchParams = new URLSearchParams()

    searchParams.append('engine_type', engineType)
    searchParams.append('engine_power', enginePower)
    searchParams.append('model', model)
    searchParams.append('year', year)
    searchParams.append('category', diagramCategory)

    if (selectedProductId) {
      searchParams.append('selected_product_id', selectedProductId)
    }

    window.parent.postMessage({
      'action': 'redirect',
      'data': {
        'url': '/diagram',
        'search': searchParams.toString()
      }
    }, '*')

    // navigate({
    //   pathname: '/diagram',
    //   search: searchParams.toString()
    // })
  }

  const filteredCategories = getFilteredCategories()

  useEffect(() => {
    if (category === 'all') {
      fetchDiagrams()
    } else {
      handleBack()
    }
  }, [])

  useEffect(() => {
    window.parent.postMessage({
      'frameHeight': 1000
    }, "*")
  }, [])

  useEffect(() => {
    if (title) {
      window.parent.postMessage({
        'frameHeight': document.body.scrollHeight
      }, "*")
    }
  }, [title])

  useEffect(() => {
    window.parent.postMessage({
      'frameHeight': document.body.scrollHeight
    }, "*")

    if (isFilterActive) {
      window.parent.postMessage({
        'action': 'filterDiagramsEnabled'
      }, '*')
    } else {
      window.parent.postMessage({
        'action': 'filterDiagramsDisabled'
      }, '*')
    }
  }, [isFilterActive])

  useEffect(() => {
    window.parent.postMessage({
      'frameHeight': document.body.scrollHeight
    }, "*")

    window.parent.postMessage({
      'action': 'filterDiagramsChanged',
      'data': {
        'category': selectedCategory,
        'search': searchText
      }
    }, '*')
  }, [selectedCategory, searchText])

  return (
    <div className='flex flex-col gap-14 my-4 mx-6 md:my-[30px] md:mx-[65px]'>
      <div className='flex flex-col gap-4'>
        <div className='text-[#005B75] text-base font-normal cursor-pointer underline' onClick={handleBack}>{`< Engine Selection`}</div>
        <div className='text-[#072743] text-3xl md:text-5xl title font-normal'>{title}</div>
        <div className='flex flex-wrap gap-[20px] max-w-[660px]'>
          <Select
            options={categories.map(item => ({ label: item.name, value: item._id }))}
            onChange={(value) => setSelectedCategory(value)}
            value={selectedCategory}
            placeholder='Select Diagram'
          />
          <input
            type='text'
            className='flex flex-1 items-center min-w-[190px] h-[48px] border-[1px] px-[10px] py-[9px] border-black appearance-none rounded-none'
            placeholder='Search parts in diagrams'
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
      </div>

      {isFilterActive ? (
        <div className='flex flex-col gap-10'>
          {filteredCategories.map(category => (
            <div key={category._id} className='flex flex-col sm:flex-row gap-[20px]'>
              <div className='flex self-center sm:self-start flex-col gap-[8px] w-[300px] cursor-pointer' onClick={() => handleOpenDiagramCategory(category._id)}>
                <img
                  src={category.diagram_image_url} 
                  className='h-[300px] border border-[#999]' 
                  alt={category.name}
                />
                <div className='self-center text-[#6E6E6E] font-normal text-base'>
                  {category.name}
                </div>
              </div>

              <div className='flex-1 overflow-x-auto'>
                <table className='w-full border border-[#D9D9D9]'>
                  <thead>
                    <tr className='bg-[#062743] text-white text-sm font-bold'>
                      <th className='text-left px-[16px] py-[11px]'>Ref. #</th>
                      <th className='text-left px-[16px] py-[11px]'>Part</th>
                      <th className='text-left px-[16px] py-[11px] hidden md:block'>SKU</th>
                      <th className='text-left px-[16px] py-[11px]'></th>
                    </tr>
                  </thead>
                  <tbody>
                    {category.products.map((product, index) => (
                      <tr
                        key={product._id}
                        className={`text-sm font-normal text-black ${index % 2 === 0 ? 'bg-[#F9F9F9]' : 'bg-[#FFFFFF]'}`}
                      >
                        <td className='px-[16px] py-[13px]'>{product.related_number ?? '-'}</td>
                        <td className='px-[16px] py-[13px]'>
                          <div className='flex flex-col gap-2'>
                            <div className='font-bold'>{product.product_name}</div>
                            <div className='block md:hidden'>{product.sku}</div>
                          </div>
                        </td>
                        <td className='px-[16px] py-[13px] hidden md:block'>{product.sku}</td>
                        <td className='px-[16px] py-[13px]'>
                        <div className='text-[#005B75] text-base font-normal cursor-pointer underline' onClick={() => handleOpenDiagramCategory(category._id, product._id)}>View in Diagram</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className='flex flex-row gap-y-14 gap-x-5 flex-wrap'>
          {categories.map(category => (
            <div key={category._id} className='flex flex-col w-[calc(50%-10px)] max-w-[200px] cursor-pointer' onClick={() => handleOpenDiagramCategory(category._id)}>
              <div className='aspect-square w-full mb-3'>
                <img src={category.diagram_image_url} className='h-full border border-gray-400 object-contain w-full' />
              </div>
              <div className='self-center text-black font-bold text-base'>{category.name}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default DiagramsPage